import React, {FC, useState} from "react"
import Papa from "papaparse"

import {Body, Label} from "@irati/ui/src/Typography"
import Card from "@irati/ui/src/Layout/Card"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Button, {IconButton} from "@irati/ui/src/Button"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import downloadCsv from "@irati/utils/downloadCsv"
import DragAndDrop from "@irati/ui/src/DragAndDrop"
import BaseCircledConnector from "@irati/ui/src/CircledConnector"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import authentication from "@irati/auth"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import isInvalidEmail from "../../../utils/isInvalidEmail"
import {DistributeRewardActionTypes, DistributeRewardState} from "../../../stores/DistributeReward/actions"
import CSVErrorMessage from "./CSVErrorMessage"
import {CSVErrors} from "../../../enums/CsvErrors"
import {BulkRewardEvents} from "../../../enums/MixpanelEvents"
import CSVErrorReportModal from "./CSVErrorReportModal"

interface Props extends InjectIntl {
  setRecipients: (recipients: string[]) => void
  dispatch: (action: DistributeRewardActionTypes) => void
  payload: DistributeRewardState
}

interface CSVContent {
  totalRows: number
  validRows: string[]
  totalInvalidRows: number
}

const MAX_ROWS = 1000
const B_TO_KB_CONVERSION = 1024

const UploadCSVContent: FC<Props> = ({formatMessage, setRecipients, dispatch, payload}) => {
  const [csvContent, setCsvContent] = useState<CSVContent | null>()
  const [fileProperties, setFileProperties] = useState<{name: string; size: number} | null>(null)

  const downloadCsvFile = () => {
    // \ufeff needed to specify the csv may contain utf-8 characters
    const csv = `\ufeff${formatMessage({id: "wallet.adminReward.bulkRewards.downloadCsvTemplate.csv"})}`
    const fileName = formatMessage({id: "wallet.adminReward.bulkRewards.downloadCsvTemplate.fileName"})
    downloadCsv(csv, fileName)
    tracking.uploadCsv(BulkRewardEvents.DownloadCsvTemplate)
  }
  const setErr = (error: CSVErrors | null) => {
    dispatch({
      type: "SET_BULK_REWARD_STATE",
      payload: {csvError: error}
    })
  }
  const onDrop = (acceptedFiles: File[]) => {
    let columnsLength: number
    const parsedRows: string[] = []
    let uniqRows: string[] = []
    const invalidEmails: string[] = []
    const [uploadedFile] = acceptedFiles
    setErr(null)
    setCsvContent(null)
    if (uploadedFile?.type !== "text/csv") {
      tracking.uploadCsv(BulkRewardEvents.CsvFormatError)
      setErr(CSVErrors.INVALID_TYPE)
      return
    }

    setFileProperties({
      name: uploadedFile.name,
      size: uploadedFile.size
    })

    Papa.parse<string[][]>(uploadedFile, {
      header: false,
      skipEmptyLines: true,
      step: (row) => {
        const {
          data: [currentRow]
        } = row
        const [emailCol] = currentRow
        if (isInvalidEmail(emailCol)) {
          invalidEmails.push(emailCol)
          return
        }

        columnsLength = currentRow.length
        parsedRows.push(emailCol)
        uniqRows = [...new Set(parsedRows)]
      },
      complete: () => {
        if (uniqRows.length > MAX_ROWS) {
          tracking.uploadCsv(BulkRewardEvents.CsvFormatError)
          return setErr(CSVErrors.MAX_ROWS_EXCEEDED)
        }

        if (uniqRows.includes(authentication.user().email)) {
          tracking.uploadCsv(BulkRewardEvents.UserMatchError)
          return setErr(CSVErrors.SELF_REWARD)
        }

        if (columnsLength > 1) {
          tracking.uploadCsv(BulkRewardEvents.CsvFormatError)
          return setErr(CSVErrors.MULTIPLE_COLUMNS)
        }

        const csvProps = {
          validRows: uniqRows,
          totalInvalidRows: invalidEmails.length,
          totalRows: uniqRows.length + invalidEmails.length
        }
        setCsvContent(csvProps)
        if (csvProps.validRows.length !== csvProps.totalRows) {
          tracking.uploadCsv(BulkRewardEvents.CsvFormatError)
          setErr(CSVErrors.EMAIL_ERRORS)
          return
        }
        setRecipients(uniqRows)
        tracking.uploadCsv(BulkRewardEvents.UploadCsv)
      },
      error: (res) => {
        console.log("[parsingCSV] Error: ", res)
      }
    })
  }

  const restartUpload = () => {
    setRecipients([])
    setCsvContent(null)
    setErr(null)
  }

  return (
    <>
      <Body>
        <FormattedMessage id="wallet.adminReward.bulkRewards.info.body" />
      </Body>
      <Card noBorder flush marginTop={2}>
        <BaseCircledConnector
          data={[
            "renderCompFromProps",
            formatMessage({id: "wallet.adminReward.bulkRewards.info.addRecipient"}),
            formatMessage({id: "wallet.adminReward.bulkRewards.info.dragAndDrop"})
          ]}
          comp={
            <Button tertiary cta onClick={downloadCsvFile}>
              <Icon icon={IconTypes.downloadReport} size={22} />
              <FormattedMessage id="wallet.adminReward.bulkRewards.info.downloadCsv" />
            </Button>
          }
        />
      </Card>

      {/* File upload area */}
      {csvContent ? (
        <Card noBorder bgColor="#F7F7F7" tiny marginTop={2}>
          <Flex justifyContent="space-between" alignItems="center" padding={1} marginBottom={3}>
            <Flex direction="column">
              <Body>{fileProperties!.name}</Body>
              <Label small marginTop={0.5}>
                {fileProperties!.size >= B_TO_KB_CONVERSION
                  ? `${parseInt(String(fileProperties!.size / B_TO_KB_CONVERSION), 10)}kb`
                  : `${fileProperties!.size}b`}
              </Label>
            </Flex>
            <IconButton icon={IconTypes.close} onClick={restartUpload} />
          </Flex>
          <Card small>
            <Flex alignItems="center" marginBottom>
              <Icon icon={IconTypes.circleTickFill} marginRight />
              <Body>
                <FormattedMessage id="wallet.adminReward.bulkRewards.rowsDetected" values={{rows: csvContent.totalRows}} />
              </Body>
            </Flex>

            {csvContent.totalInvalidRows > 0 && (
              <>
                <Flex alignItems="center" marginBottom>
                  <Icon icon={IconTypes.circleAlertFill} marginRight />
                  <Body color={mainTheme.colors.tertiary.r_400}>
                    <FormattedMessage id="wallet.adminReward.bulkRewards.rowsWithErrors" values={{rows: csvContent.totalInvalidRows}} />
                  </Body>
                </Flex>
                <Body>
                  <FormattedMessage id="wallet.adminReward.bulkRewards.hint" />
                </Body>
              </>
            )}
          </Card>
        </Card>
      ) : (
        <>
          <CSVErrorMessage error={payload.bulkRewardState.csvError} />
          <DragAndDrop onDrop={onDrop} />
        </>
      )}

      <CSVErrorReportModal
        failedEmails={payload.bulkRewardState.failedEmails}
        totalEmails={payload.userEmails.length}
        dispatch={dispatch}
        resetCSV={restartUpload}
      />
    </>
  )
}

export default injectIntl(UploadCSVContent)
